import React, { useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { SquarePlus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import AppCardSkeleton from './AppCardSkeleton';
import { useAppIdeaFavorites, type AppIdea } from '@/hooks/app-library/use-app-idea-favorites';
import { useDeleteAppIdea } from '@/hooks/app-library/use-fetch-app-ideas';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import AppCard from './AppCard';
import { useAppLibrary } from './AppLibraryContext';
import DashboardCard from '@/components/ui/DashboardCard';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { type AppItem } from '@/hooks/app-library/use-app-favorites';
import { AppIdeaContentLightbox } from '@/components/app-ideas/lightbox';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { toast } from '@/hooks/errors/use-toast';
import { useAppIdeaPDF } from '@/hooks/pdf';

interface AppIdeasContainerProps {
  onRequestApp?: (category?: string) => void;
}

export interface AppIdeasContainerRef {
  openLightboxForIdea: (ideaId: string) => void;
}

const AppIdeasContainer = forwardRef<AppIdeasContainerRef, AppIdeasContainerProps>(({ onRequestApp }, ref) => {
  const { selectedCategory } = useAppLibrary();
  const { appIdeas, isLoading: appIdeasLoading, hasError: appIdeasError } = useAppIdeaFavorites();
  const deleteAppIdeaMutation = useDeleteAppIdea();
  const { generatePDF } = useAppIdeaPDF();
  const isMobile = useIsMobile();
  const [isLightboxOpen, setLightboxOpen] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState<AppIdea | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const handleOpenLightbox = (idea: AppItem) => {
    setSelectedIdea(idea as AppIdea);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setSelectedIdea(null);
  };

  // Handle download functionality
  const handleDownload = (idea: AppIdea) => {
    // Generate PDF instead of markdown
    generatePDF(idea);
  };

  // Handle delete functionality with confirmation dialog
  const handleDeleteRequest = async (idea: AppIdea) => {
    // The idea parameter is passed but we use selectedIdea from state
    // This ensures we're deleting the currently viewed idea in the lightbox
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteAppIdea = async () => {
    if (!selectedIdea) return;
    
    try {
      await deleteAppIdeaMutation.mutateAsync(selectedIdea.id);
      setShowDeleteConfirmation(false);
      handleCloseLightbox();
      toast({
        title: "Success",
        description: "App idea deleted successfully",
      });
    } catch (error: any) {
      console.error('Failed to delete app idea:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete app idea",
        variant: "destructive",
      });
    }
  };

  const cancelDeleteAppIdea = () => {
    setShowDeleteConfirmation(false);
  };

  // Get the live version of the selected idea from the cache
  const liveSelectedIdea = useMemo(() => {
    if (!selectedIdea || !appIdeas) return selectedIdea;
    
    // Find the current version of the selected idea from the live data
    const liveIdea = appIdeas.find(idea => idea.id === selectedIdea.id);
    return liveIdea || selectedIdea; // Fallback to the original if not found
  }, [selectedIdea, appIdeas]);

  // Expose method to parent via ref
  useImperativeHandle(ref, () => ({
    openLightboxForIdea: (ideaId: string) => {
      console.log('[AppIdeasContainer] Opening lightbox for idea:', ideaId);
      
      // Find the app idea by ID
      const idea = appIdeas?.find(appIdea => appIdea.id === ideaId);
      if (idea) {
        console.log('[AppIdeasContainer] Found idea, opening lightbox:', idea);
        setSelectedIdea(idea);
        setLightboxOpen(true);
      } else {
        console.warn('[AppIdeasContainer] App idea not found in current list:', ideaId);
        console.log('[AppIdeasContainer] Available app ideas:', appIdeas?.map(a => ({ id: a.id, name: a.name })));
        throw new Error(`App idea with ID ${ideaId} not found in current list`);
      }
    }
  }), [appIdeas]);

  // Memoize filtered app ideas
  const filteredAppIdeas = useMemo(() => {
    if (!appIdeas) return [];
    
    return appIdeas.filter(appIdea => {
      // Apply category filter, but ignore 'favorites' since App Requests use a different favorites system
      if (selectedCategory === 'all' || selectedCategory === 'favorites') return true;
      if (selectedCategory) {
        return appIdea.category === selectedCategory;
      }
      return true;
    });
  }, [appIdeas, selectedCategory]);

  // Memoize the app ideas cards
  const appIdeaCards = useMemo(() => {
    if (appIdeasLoading) {
      return (
        <motion.div 
          className="grid grid-cols-1 2xl:grid-cols-2 gap-2 md:gap-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {Array.from({ length: 3 }).map((_, index) => (
            <motion.div
              key={`idea-skeleton-${index}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.2, 
                delay: index * 0.05,
                ease: [0.25, 0.1, 0.25, 1.0]
              }}
              className="rounded-lg"
            >
              <AppCardSkeleton />
            </motion.div>
          ))}
        </motion.div>
      );
    }

    if (appIdeasError) {
      return (
        <div className="text-center py-8">
          <p className="text-muted-foreground">Failed to load app requests. Please try again.</p>
        </div>
      );
    }

    if (filteredAppIdeas.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-muted-foreground mb-4">
            {selectedCategory === 'all' 
              ? 'No app requests yet. Be the first to submit one!' 
              : `No app requests in the ${selectedCategory} category yet.`
            }
          </p>
          {onRequestApp && (
            <Button onClick={() => onRequestApp(selectedCategory || undefined)} variant="outline">
              <SquarePlus size={16} className="mr-2" />
              {selectedCategory === 'all' ? 'Submit the First Request' : `Add a Request for ${selectedCategory}`}
            </Button>
          )}
        </div>
      );
    }

    return (
      <motion.div 
        className="grid grid-cols-1 2xl:grid-cols-2 gap-2 md:gap-8"
        layout
        style={{ 
          perspective: '1000px',
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden'
        }}
      >
        <AnimatePresence mode="popLayout">
          {filteredAppIdeas.map((appIdea) => (
            <motion.div 
              key={appIdea.id}
              layoutId={`app-idea-${appIdea.id}`}
              className="rounded-lg"
              initial={{ 
                opacity: 0, 
                scale: 0.9,
                y: 10
              }}
              animate={{ 
                opacity: 1, 
                scale: 1,
                y: 0
              }}
              exit={{ 
                opacity: 0, 
                scale: 0.9,
                y: -10
              }}
              transition={{
                duration: 0.2,
                ease: [0.25, 0.1, 0.25, 1.0],
                layout: {
                  duration: 0.3,
                  ease: [0.25, 0.1, 0.25, 1.0]
                }
              }}
            >
              <AppCard 
                app={appIdea as unknown as AppItem} // Type assertion since we transformed the interface
                isPinModeActive={false} // App ideas can't be pinned
                isGreyedOut={false} // App ideas are never greyed out
                isAppIdea={true} // This is an app idea, use app idea favorites system
                onAppIdeaClick={handleOpenLightbox}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
    );
  }, [filteredAppIdeas, appIdeasLoading, appIdeasError, selectedCategory, onRequestApp]);

  return (
    <>
      <DashboardCard variant="amber" className="p-3 md:p-6 md:pb-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-50/60 to-amber-50/60 dark:from-orange-950/20 dark:to-amber-950/20 rounded-xl border border-orange-200/50 dark:border-orange-800/30 p-4 md:p-6 mb-2 md:mb-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-col md:flex-row md:items-baseline md:gap-4">
              <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-600 dark:from-orange-400 dark:to-amber-400">
                App Requests
              </h3>
              <p className="text-sm text-muted-foreground/80 mt-1 md:mt-0 font-medium">
                Apps from the community
              </p>
            </div>
            
            {/* Add a Request Button */}
            {onRequestApp && appIdeas && appIdeas.length > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onRequestApp()}
                      className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/50 dark:to-amber-950/50 border-orange-200 dark:border-orange-800 hover:bg-gradient-to-r hover:from-orange-100 hover:to-amber-100 dark:hover:from-orange-900/50 dark:hover:to-amber-900/50 text-orange-700 dark:text-orange-300 hover:text-orange-800 dark:hover:text-orange-200 transition-all duration-200"
                    >
                      <SquarePlus size={16} className="mr-2" />
                      Add a Request
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-gradient-to-br from-orange-500 to-amber-500 text-white border border-white/20 shadow-lg">
                    Submit a new app request
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
        
        {/* App Requests grid */}
        <div className={`w-full ${isMobile ? 'min-h-[200px]' : 'min-h-[300px]'}`}>
          {appIdeaCards}
        </div>
      </DashboardCard>

      <AppIdeaContentLightbox
        idea={liveSelectedIdea}
        isOpen={isLightboxOpen}
        onClose={handleCloseLightbox}
        allIdeas={appIdeas || []}
        onNavigate={(idea) => setSelectedIdea(idea as AppIdea)}
        isLoading={appIdeasLoading}
        hasNextPage={false}
        isFetchingNextPage={false}
        onDelete={handleDeleteRequest}
        onDownload={handleDownload}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirmation}
        onConfirm={confirmDeleteAppIdea}
        onCancel={cancelDeleteAppIdea}
        title="Delete App Idea"
        description={`Are you sure you want to delete "${selectedIdea?.name || selectedIdea?.generated_title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isConfirmLoading={deleteAppIdeaMutation.isPending}
        confirmVariant="red"
      />
    </>
  );
});

AppIdeasContainer.displayName = 'AppIdeasContainer';

export default AppIdeasContainer;
